/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { NotificationService } from '@alfresco/adf-core';
import { PageHeaderComponent, PageStateComponent, ThaiDatePipe } from '@alfresco/aca-shared';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { PdpaFinding, PdpaReport, PdpaService, PdpaUnreadReason, PiiKindName } from './pdpa.service';

/** How often a running scan is asked how far it got. */
const POLL_MS = 3000;

export const PII_KIND_NAMES: PiiKindName[] = ['thaiId', 'card', 'bankAccount', 'phone', 'email'];
const UNREAD_REASONS: PdpaUnreadReason[] = ['not-indexed', 'unsupported', 'too-large', 'failed'];
/** Levels that let most staff read a document — personal data there is the first thing to fix. */
const OPEN_LEVELS = new Set(['', 'public', 'internal']);

/**
 * PDPA report: which documents hold personal data, so the organisation knows
 * where it keeps Thai IDs, card and account numbers, phones and emails, and can
 * redact, restrict or remove them.
 *
 * The scan runs on the jobs sidecar over the whole repository; this screen
 * starts it, follows it while it runs and shows the findings. Values are masked
 * by the sidecar — the screen never receives one in full.
 */
@Component({
  selector: 'app-pdpa-report',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatProgressBarModule,
    PageHeaderComponent,
    PageStateComponent,
    ThaiDatePipe,
    TranslatePipe
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './pdpa-report.component.html',
  styleUrls: ['./pdpa-report.component.scss']
})
export class PdpaReportComponent implements OnInit, OnDestroy {
  /** Inside the settings page: no page header of its own. */
  @Input() embedded = false;

  private readonly pdpa = inject(PdpaService);
  private readonly translate = inject(TranslateService);
  private readonly notification = inject(NotificationService);
  private readonly router = inject(Router);

  readonly kinds = PII_KIND_NAMES;
  readonly reasons = UNREAD_REASONS;

  readonly report = signal<PdpaReport | null>(null);
  readonly loading = signal(true);
  readonly failed = signal('');
  readonly starting = signal(false);
  readonly kindFilter = signal<PiiKindName | 'all'>('all');
  readonly openOnly = signal(false);
  readonly showUnread = signal(false);

  private timer?: ReturnType<typeof setTimeout>;

  readonly running = computed(() => this.report()?.status === 'running');
  readonly progress = computed(() => {
    const report = this.report();
    return report?.total ? Math.min(100, Math.round((report.scanned / report.total) * 100)) : 0;
  });
  readonly unreadTotal = computed(() => Object.values(this.report()?.unreadCounts ?? {}).reduce((sum, n) => sum + (n ?? 0), 0));
  readonly openCount = computed(() => (this.report()?.findings ?? []).filter((f) => this.isOpen(f)).length);

  readonly visible = computed(() => {
    const kind = this.kindFilter();
    return (this.report()?.findings ?? []).filter((f) => (kind === 'all' || !!f.counts[kind]) && (!this.openOnly() || this.isOpen(f)));
  });

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    clearTimeout(this.timer);
  }

  load(): void {
    clearTimeout(this.timer);
    this.pdpa.report().subscribe({
      next: (report) => {
        this.loading.set(false);
        this.failed.set('');
        this.report.set(report);
        this.followIfRunning();
      },
      error: (err) => {
        this.loading.set(false);
        this.failed.set(this.translate.instant(err?.status === 403 ? 'DMS.PDPA.FORBIDDEN' : 'DMS.PDPA.LOAD_FAILED'));
      }
    });
  }

  start(): void {
    if (this.starting() || this.running()) {
      return;
    }
    this.starting.set(true);
    this.pdpa.start().subscribe({
      next: (report) => {
        this.starting.set(false);
        this.report.set(report);
        this.followIfRunning();
      },
      error: (err) => {
        this.starting.set(false);
        if (err?.status === 409) {
          // someone else started one a moment ago: follow theirs
          this.load();
          return;
        }
        this.notification.showError(this.translate.instant('DMS.PDPA.START_FAILED'));
      }
    });
  }

  cancel(): void {
    this.pdpa.cancel().subscribe({
      next: () => this.load(),
      error: () => this.load()
    });
  }

  private followIfRunning(): void {
    clearTimeout(this.timer);
    if (this.running()) {
      this.timer = setTimeout(() => this.load(), POLL_MS);
    }
  }

  openDocument(nodeId: string): void {
    void this.router.navigate(['/document', nodeId]);
  }

  // ---- view helpers ---------------------------------------------------------

  isOpen(finding: PdpaFinding): boolean {
    return OPEN_LEVELS.has(finding.confidentiality ?? '');
  }

  kindKey(kind: string): string {
    return 'DMS.PDPA.KIND_' + kind.toUpperCase();
  }

  levelKey(finding: PdpaFinding): string {
    return 'DMS.PDPA.LEVEL_' + (finding.confidentiality || 'none').toUpperCase();
  }

  reasonKey(reason: string): string {
    return 'DMS.PDPA.REASON_' + reason.toUpperCase().replace('-', '_');
  }

  statusKey(report: PdpaReport): string {
    return 'DMS.PDPA.STATUS_' + report.status.toUpperCase();
  }

  /** The masked examples of one kind, for the tooltip. */
  samplesOf(finding: PdpaFinding, kind: PiiKindName): string {
    return (finding.samples[kind] ?? []).join(' · ');
  }

  kindsOf(finding: PdpaFinding): PiiKindName[] {
    return PII_KIND_NAMES.filter((kind) => !!finding.counts[kind]);
  }

  trackByNode(_: number, item: { nodeId: string }): string {
    return item.nodeId;
  }
}
